import React from 'react';
import { NavLink, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { Menu, MenuItem } from '@mui/material';
import Badge from '@mui/material/Badge';
import dayjs from 'dayjs';
import api from '../api';
import { color } from '../styles';
import { IGetNursingHomeRes } from '../types/api/nursingHome/getNursingHome';
import { residentListSettingsSlice } from '../redux/config/residentListSettings';
import { selfDataSelector, selfDataSlice } from '../redux/config/selfData';
import { currentNursingHomeSelector, currentNursingHomeSlice } from '../redux/config/currentNursingHome';
import { useFetchAnnouncements } from '@/hooks/api/announcement/useFetchAnnouncements';

interface Props {
  nursingHomes: IGetNursingHomeRes[] | null;
  isOpenLeftnav: boolean;
  onOpen: () => void;
  fetchBadge: (nursingHomeId: string, unitId: number | null) => void;
}

const Container = css`
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 62px;
  width: 100%;
  padding: 0 16px;
  background-color: var(--surface-global-header);
  border-bottom: 2px solid ${color.bg_light_gray};
`;

const LeftContainer = css`
  display: flex;
  align-items: center;
`;

const RightContainer = css`
  span {
    color: ${color.black};
  }
`;

const NursingHomeName = css`
  font-weight: bold;
  font-size: 18px;
  line-height: 26px;
`;

const NursingHomeHeaderLine = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const NursingHomeTextBlock = css`
  display: flex;
  flex-direction: column;
`;

const TrialModeLabel = css`
  position: relative;
  top: 1px;
  white-space: nowrap;
  font-size: 12px;
  color: ${color.green};
  border: 1px solid ${color.green};
  border-radius: 9999px;
  padding: 2px 8px;
`;

const UnitName = css`
  font-size: 12px;
  line-height: 12px;
`;

const Img = css`
  width: 32px;
  height: 32px;
  margin: 0 4px;
`;

const HamburgerIcon = css`
  margin: 12px;
`;

const HeaderLink = css`
  text-decoration: none;
  color: ${color.dark_gray};
  font-size: 14px;
  margin-right: 6px;
  &:hover {
    color: ${color.green};
  }
`;

const AnnouncementWrapper = css`
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
`;

export const GlobalHeader = React.memo(({ nursingHomes, isOpenLeftnav, onOpen, fetchBadge }: Props) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const selfData = useSelector(selfDataSelector);
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filteredNursingHomes, setFilteredNursingHomes] = React.useState<IGetNursingHomeRes[] | null>(null);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { url } = useRouteMatch();
  const { announcements } = useFetchAnnouncements(
    true,
    i18n.language.startsWith('ja') ? 'ja' : 'en',
    dayjs().subtract(14, 'day').toISOString()
  );

  const showAnnouncementBadge = !!(announcements && announcements.length);

  const handleFacilityOpenButton = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFacilityClick = async (nursingHome: IGetNursingHomeRes) => {
    const units = await api.get('/nursing-homes/:nursingHomeId/nursing-home-units', {
      params: { nursingHomeId: nursingHome.id },
    });
    dispatch(selfDataSlice.actions.setSelfUnits(units));
    dispatch(currentNursingHomeSlice.actions.setCurrentNursingHome(nursingHome));
    dispatch(residentListSettingsSlice.actions.resetFilterCondition());
    localStorage.setItem('unit', 'null');
    localStorage.setItem('condition', 'all');
    // 施設変更時は変更するIDをもとにバッジを表示する
    fetchBadge(String(nursingHome.id), null);
    handleClose();
    history.push(`/nursinghome/${nursingHome.id}/residents`);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickLanguage = () => {
    i18n.changeLanguage(i18n.language === 'ja' ? 'en' : 'ja');
  };

  React.useEffect(() => {
    if (nursingHomes !== null) {
      // 現在のNursingHomeIdと一致するものはListから除外
      const filteredNursingHomes = nursingHomes?.filter((nursingHome) => {
        return nursingHome.id !== +nursingHomeId;
      });
      setFilteredNursingHomes(filteredNursingHomes);
    }
  }, [nursingHomes, nursingHomeId]);

  return (
    <>
      <div css={Container}>
        <div css={LeftContainer}>
          {!isOpenLeftnav && <MenuIcon css={HamburgerIcon} onClick={onOpen} />}
          <div css={NursingHomeHeaderLine}>
            <div css={NursingHomeTextBlock}>
              <div css={NursingHomeName}>{currentNursingHome.name}</div>
              {!!selfData.selfUnit.name && <div css={UnitName}>{selfData.selfUnit.name}</div>}
            </div>
            {currentNursingHome.contractStatus === 'demo' && <div css={TrialModeLabel}>{'トライアルモード'}</div>}
          </div>
        </div>
        <div css={RightContainer}>
          <div css={AnnouncementWrapper}>
            <Badge color='error' variant='dot' overlap='rectangular' invisible={!showAnnouncementBadge}>
              <NavLink css={HeaderLink} to={`${url}/announcements`}>
                {t('information', 'お知らせ')}
              </NavLink>
            </Badge>
          </div>
          {!selfData.selfUser.hasUnitPerm && (
            <img css={Img} src='/images/header/icon_facility.svg' alt='施設アイコン' />
          )}
          {selfData.selfUser.professionalRole !== 'member' && nursingHomes && (
            <>
              <Button onClick={handleFacilityOpenButton}>
                <span>
                  {t('facilitiesChange', '施設変更')}
                  <ArrowDropDownIcon />
                </span>
              </Button>
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
                {filteredNursingHomes &&
                  filteredNursingHomes.map((nursingHome, key) => (
                    <MenuItem key={key} onClick={() => handleFacilityClick(nursingHome)}>
                      {nursingHome.name}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          )}
        </div>
      </div>
    </>
  );
});
